import Errored from './Errored';
import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import { Typography } from '@material-ui/core';
import ActionText from './ActionText';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import GS from '../grafsync/grafsync';
import { globalStyles } from './style/gstheme';
import { debounce, makeCancelable } from './utils/utils';

const styles = theme => {
    return Object.assign({
        showing: {
            marginTop: 6,
            fontSize: '12px',
            paddingLeft: 6,
            color: theme.palette.primary.main
        },
        unselectedResult: {
            margin: '0px 0px 12px 0px',
            padding: '6px 12px',
            border: 'solid 1px #e5e5e5',
            borderRadius: 4,
            alignItems: 'flex-start',
            cursor: 'pointer',
            backgroundColor: '#f8f8f8',
            '&:hover': {
                backgroundColor: '#e0e0e0'
            }
        },
        unselectedResultHover: {
            margin: '0px 0px 12px 0px',
            padding: '6px 12px',
            border: 'solid 1px #e5e5e5',
            borderRadius: 4,
            alignItems: 'flex-start',
            cursor: 'pointer',
            backgroundColor: '#e0e0e0'
        },
        selectedResult: {
            margin: '0px 0px 12px 0px',
            padding: '6px 12px',
            border: 'solid 1px #e5e5e5',
            alignItems: 'flex-start',
            color: '#212121',
            opacity: 0.6
        },
        searchBox: {
            height: 'calc(100% - 4px)',
            paddingTop: 4,
            overflow: 'hidden'
        },
        searchResults: {
            margin: '12px 0px 0px 0px',
            paddingRight: 20,
            height: 'calc(100% - 80px)',
            overflowY: 'scroll'
        }
    }, globalStyles);
};

class SearchDBpediaSelect extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isSearching: false,
            searchPending: false,
            results: [],
            scroll: false,
            highlighted: -1,
            searchID: null,
            text: this.props.initialText,
            isErrored: null
        };
        this.textInput = React.createRef();
        this.cancellable = null;
        this.resourceRef = React.createRef();
    }

    componentDidMount() {
        if (this.state.text !== "") {
            this.setState({ searchPending: true }, () => {
                this.find(this.state.text, this.props.lang);
            });
        }
    }

    componentDidUpdate() {
        if (this.state.scroll && this.resourceRef && this.resourceRef.current) {
            this.resourceRef.current.scrollIntoView({
                block: 'center',
                inline: 'center',
            });
            this.setState({ scroll: false });
        }
    }

    find = (value) => {
        this.setState({ isSearching: true }, async () => {
            try {
                const data = await this.doSearch(value).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                // Ignore 'old' 
                if (data.uuid && data.uuid !== this.state.searchID) return;
                this.setState({
                    isSearching: false,
                    searchPending: false,
                    results: data.docs
                });
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                    this.setState({
                        isErrored: e,
                        isSearching: false,
                        searchPending: false,
                        results: []
                    });
                }
            }
        });
    };

    handleCursor = (event) => {
        const hl = this.state.highlighted;
        switch (event.keyCode) {
            case 13:
                if (hl !== -1) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.chosen(this.state.results[hl].resource[0]);
                }
                break;
            case 38:
                event.preventDefault();
                event.stopPropagation();
                if (hl > 0) this.setState({ highlighted: hl - 1, scroll: true });
                break;
            case 40:
                event.preventDefault();
                event.stopPropagation();
                if (hl < this.state.results.length - 1) this.setState({ highlighted: hl + 1, scroll: true });
                break;
            default:
                break;
        }
    };

    handleChange = (event) => {
        const uncleanedText = event.target.value;
        let text = uncleanedText.replace(/[[\]]/g, '').trimLeft();
        this.props.updateSearchText(text);
        if (text !== this.state.text) {
            this.setState({ text, searchPending: true, highlighted: -1 });
            if (text === "") {
                this.setState({ results: [], searchPending: false });
                return;
            }
            debounce(this.find, this, [text, this.props.lang]);
        }
    };

    doSearch(text) {
        const searchID = uuidv4();
        this.setState({ searchID });
        const url = "https://lookup.dbpedia.org/api/search?maxResults=100&format=JSON_RAW&highlight=false&query=" + text;
        this.cancellable = makeCancelable(GS.doGetRequest(url, searchID));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    chosen = (id) => this.props.chosen(id);

    clearError = () => this.setState({ isErrored: false });

    handleClick = (event) => event.stopPropagation();

    render() {

        const { results, isSearching, text, searchPending, highlighted, isErrored } = this.state;
        const { classes, exclude } = this.props;

        if (isErrored) return <Errored dataTest="db-error" data={isErrored} clearError={this.clearError} />;

        return (
            <div className={classes.searchBox} onKeyDown={this.handleCursor}>
                <TextField
                    style={{ width: '100%', marginTop: '0px' }}
                    margin="dense"
                    autoFocus={true}
                    inputRef={this.textInput}
                    type="text"
                    value={text}
                    onChange={this.handleChange}
                    placeholder={"Search DBpedia"}
                    variant="outlined"
                    InputLabelProps={{
                        className: classes.inputLabel,
                        classes: {
                            shrink: classes.inputLabelShrink
                        }
                    }}
                    InputProps={
                        {
                            className: classes.inputLabel,
                            inputProps: {
                                "data-test": "dbpedia-input",
                                maxLength: 100
                            },
                            endAdornment:
                                <InputAdornment position="end">
                                    {!isSearching && text !== "" ?
                                        <Tooltip title="Clear">
                                            <span>
                                                <ClearIcon data-test="clear" className={classes.inputIconHighlighted} onClick={
                                                    () => {
                                                        this.handleChange({
                                                            target: {
                                                                value: ""
                                                            }
                                                        });
                                                        this.textInput && this.textInput.current && this.textInput.current.focus();
                                                    }
                                                } />
                                            </span>
                                        </Tooltip>
                                        :
                                        (isSearching ?
                                            <Tooltip title="Searching for results">
                                                <span>
                                                    <CircularProgress color="secondary" size={20} />
                                                </span>
                                            </Tooltip>
                                            :
                                            <Tooltip title="Search">
                                                <span>
                                                    <SearchIcon className={classes.inputIconHighlighted} />
                                                </span>
                                            </Tooltip>
                                        )
                                    }
                                </InputAdornment>
                        }
                    } />
                {results && results.length > 0 && !isSearching ?
                    <List data-test="list-results" dense className={classes.searchResults}>
                        {
                            results.map((result, index) => {
                                let clickable = exclude ?
                                    !exclude.includes(result.resource[0])
                                    :
                                    true;
                                return <ListItem key={index}
                                    ref={highlighted === index ? this.resourceRef : null}
                                    onClick={clickable ? () => this.chosen(result.resource[0]) : null}
                                    data-test={clickable ? "list-item-" + index : "list-item-" + index + "-invalid"}
                                    className={clickable ?
                                        highlighted === index ?
                                            classes.unselectedResultHover
                                            :
                                            classes.unselectedResult
                                        :
                                        classes.selectedResult}>
                                    <ListItemText disableTypography data-test="list-item-text" primary={
                                        <div>
                                            <Typography data-test="resource-label" variant="body2">{result.label}</Typography>
                                            <Typography paragraph variant="caption" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <Tooltip title="Open link in a new tab">
                                                    <a data-test="resource-link" href={result.resource[0]} target="_new" onClick={this.handleClick}>
                                                        {result.resource[0]}
                                                    </a>
                                                </Tooltip>
                                            </Typography>
                                            <div style={{ display: 'flex', maxHeight: 150, overflow: 'scroll' }}>
                                                {result.typeName &&
                                                    <div style={{ paddingRight: 20 }}>
                                                        {result.typeName.sort().map((tn, index) => <Chip
                                                            key={index}
                                                            data-test={"type-" + index}
                                                            color="primary"
                                                            avatar={<Avatar style={{ backgroundColor: '#01579B' }}>en</Avatar>}
                                                            clickable={false}
                                                            label={tn}
                                                            className={classes.chip}
                                                        />)}
                                                    </div>
                                                }
                                                <Typography data-test="resource-desc" variant="body2" style={{ paddingRight: 8 }}>{result.comment}</Typography>
                                            </div>
                                        </div>
                                    } />
                                </ListItem>;
                            })}
                    </List>
                    :
                    <div data-test="no-results" >
                        {!isSearching && !searchPending &&
                            <ActionText offset="100px">{text === "" ?
                                "Enter text to search."
                                :
                                "No results."
                            }</ActionText>
                        }
                        {searchPending && <ActionText offset="100px">Searching ...</ActionText>}
                    </div>
                }
            </div>
        );
    }
}

SearchDBpediaSelect.defaultProps = {
    lang: "en",
    initialText: ""
};

SearchDBpediaSelect.propTypes = {
    initialText: PropTypes.string,
    lang: PropTypes.string,
    exclude: PropTypes.array,
    updateSearchText: PropTypes.func.isRequired,
    dataTest: PropTypes.string,
    chosen: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(SearchDBpediaSelect);